import { CdkConnectedOverlay, OverlayModule } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { InputAutocompleteItem } from '@shared/models';
import { DeviceService } from '@shared/services';
import { IconTypes } from '@shared/v2/models';
import { DropdownMenuComponent, DropdownMenuItemComponent } from '../dropdown';
import { SdvorIconComponent } from '../icon';
import { SdvorLoaderComponent } from '../loader';

@Component({
  selector: 'sd-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  styleUrls: ['./input-autocomplete.component.scss'],
  imports: [
    NgTemplateOutlet,
    FormsModule,
    SdvorIconComponent,
    OverlayModule,
    DropdownMenuItemComponent,
    DropdownMenuComponent,
    SdvorLoaderComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputAutocompleteComponent implements AfterViewInit {
  opened = signal(false);

  @ViewChild(CdkConnectedOverlay) overlayDir: CdkConnectedOverlay;
  @ViewChild('autocompleteRef', { read: ElementRef }) autocompleteRef: ElementRef;

  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceService = inject(DeviceService);

  protected readonly iconTypes = IconTypes;
  protected readonly isMobileSmDown = toSignal(this.deviceService.isMobileSmDown$);

  _items: InputAutocompleteItem<unknown>[];

  get items(): InputAutocompleteItem<unknown>[] {
    return this._items;
  }

  @Input() set items(value: InputAutocompleteItem<unknown>[]) {
    if (!value) {
      return;
    }

    this._items = value;
    this.openSuggestMenu(value);
  }

  @Input() loading: boolean;
  @Input() value = '';
  @Input() placeholder = 'поиск';
  @Input() disabled = false;
  @Input() itemTemplate!: TemplateRef<any>;
  @Input() minLength = 0;
  @Input() maxLength = 255;

  @Output() changeInput = new EventEmitter<any>();
  @Output() setInput = new EventEmitter<unknown>();

  ngAfterViewInit(): void {
    this.overlayDir.attach.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const originWidth = this.autocompleteRef.nativeElement.offsetWidth;

      this.overlayDir.overlayRef.updateSize({
        width: originWidth,
      });
    });
  }

  onInputFocus(): void {
    if (!this.items?.length) {
      return;
    }

    this.open();
  }

  onInputValueChange(): void {
    const value = this.value.trim();

    this.setInput.emit(undefined);
    this.changeInput.emit(value.length >= this.minLength ? this.value : '');
  }

  openSuggestMenu(items: InputAutocompleteItem<unknown>[]): void {
    if (items.length > 0) {
      this.open();
    }
  }

  onInputEnterKeyDown(): void {
    if (!this.items.length) {
      return;
    }

    const item = this.items[0];

    this.value = item.label;
    this.setInput.emit(item.value);
    this.close();
  }

  onDropdownMenuItemSelect(item: InputAutocompleteItem<unknown>): void {
    this.setInput.emit(item.value);
    this.value = item.label;
  }

  close(): void {
    this.opened.set(false);
  }

  open(): void {
    this.opened.set(true);
  }
}
